import PropTypes from "prop-types";
import { createContext, useEffect, useState } from "react";
import { encryptCryptoJs, decryptCryptoJs } from "../Helper/crypto";
import { AuthService } from "@/core/http/services/AuthService";

const AuthContext = createContext();
const LOGIN_KEY = process.env.REACT_APP_KEY_LOGIN_DATA;

const normalizeRole = (role = "") => {
  const raw = String(role || "")
    .trim()
    .toUpperCase();

  if (raw === "SUPERADMIN" || raw === "SUPER-ADMIN" || raw === "SUPER_ADMIN") {
    return "SUPER_ADMIN";
  }

  return raw;
};

export const AuthContextProvider = ({ children }) => {
  const [userLoggedData, setUserLoggedData] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const persistUserData = async (nextUserData) => {
    try {
      const encrypted = await encryptCryptoJs(JSON.stringify(nextUserData));
      localStorage.setItem(LOGIN_KEY, encrypted);
    } catch (error) {
      console.error("Falha ao persistir dados do usuário", error);
    }
  };

  const updateUserFromPlayer = async (playerPatch = {}) => {
    if (!userLoggedData) return;

    const nextPlayer = {
      ...(userLoggedData?.player || {}),
      ...(playerPatch || {}),
    };

    const category = nextPlayer?.category || userLoggedData?.category || null;

    const nextUserData = {
      ...userLoggedData,
      player: nextPlayer,
      avatarUrl: nextPlayer?.avatarUrl || userLoggedData?.avatarUrl || "",
      name: nextPlayer?.name || userLoggedData?.name || "Jogador",
      email: nextPlayer?.email || userLoggedData?.email || "",
      phone:
        nextPlayer?.phone !== undefined && nextPlayer?.phone !== null
          ? nextPlayer.phone
          : userLoggedData?.phone || "",
      birthDate: nextPlayer?.birthDate || userLoggedData?.birthDate || null,
      category,
      currentCategory: {
        ...(userLoggedData?.currentCategory || {}),
        id: category?.id || userLoggedData?.currentCategory?.id || null,
        category: category?.name || userLoggedData?.currentCategory?.category || null,
        level: category?.level || userLoggedData?.currentCategory?.level || null,
        type: category?.type || userLoggedData?.currentCategory?.type || "",
        ranking: {
          ...(userLoggedData?.currentCategory?.ranking || {}),
        },
      },
    };

    await persistUserData(nextUserData);
    setUserLoggedData(nextUserData);
  };

  useEffect(() => {
    const loadUserFromStorage = async () => {
      try {
        const stored = localStorage.getItem(LOGIN_KEY);
        if (stored) {
          const decrypted = await decryptCryptoJs(stored);
          const parsed = JSON.parse(decrypted);

          if (parsed.lastLoginAt) {
            const lastLoginAt = new Date(parsed.lastLoginAt);
            const now = new Date();

            const eightHoursInMillis = 8 * 60 * 60 * 1000;

            if (now - lastLoginAt > eightHoursInMillis) {
              localStorage.removeItem(LOGIN_KEY);
              setUserLoggedData(null);
            } else {
              setUserLoggedData({
                ...parsed,
                role: normalizeRole(parsed?.role),
                clubs: Array.isArray(parsed?.clubs) ? parsed.clubs : [],
                selectedClubId: parsed?.selectedClubId ?? null,
              });
            }
          } else {
            localStorage.removeItem(LOGIN_KEY);
            setUserLoggedData(null);
          }
        }
      } catch (error) {
        console.error("Falha ao carregar dados do usuário do storage", error);
        localStorage.removeItem(LOGIN_KEY);
        setUserLoggedData(null);
      } finally {
        setIsAuthLoading(false);
      }
    };
    loadUserFromStorage();
  }, []);

  const signIn = async (payload, { inviteToken } = {}) => {
    const { status, data, message } = await AuthService.login(payload, {
      inviteToken,
    });

    if (status !== 200 && status !== 201) {
      return {
        success: false,
        message: message || "Nao foi possivel realizar o login.",
      };
    }

    const role = normalizeRole(data?.user?.role);
    const isAdmin = role === "ADMIN" || role === "SUPER_ADMIN";
    if (!data?.token || !data?.user || (!isAdmin && !data?.player)) {
      return {
        success: false,
        message: "Resposta de autenticacao incompleta.",
      };
    }

    await signInWithToken(data);

    return {
      success: true,
      message: "Login realizado com sucesso!",
    };
  };

  const signInWithToken = async (authResponse) => {
    const token = authResponse?.token || "";
    const user = authResponse?.user || {};
    const player = authResponse?.player || {};
    const category = player?.category || null;
    const clubs = Array.isArray(authResponse?.clubs)
      ? authResponse.clubs
      : Array.isArray(player?.clubs)
        ? player.clubs
        : [];

    const selectedClubId =
      authResponse?.selectedClubId ??
      authResponse?.adminOfClub?.id ??
      (clubs.length === 1 ? clubs[0]?.id : null);

    const UserData = {
      token,
      jwtToken: token,
      user,
      player,
      id: user?.id || player?.id || null,
      userId: user?.id || null,
      playerId: player?.id || null,
      email: player?.email || user?.email || "",
      name: player?.name || "Jogador",
      role: normalizeRole(user?.role || ""),
      birthDate: player?.birthDate || null,
      clubs,
      selectedClubId,
      category,
      currentCategory: {
        id: category?.id || null,
        category: category?.name || null,
        level: category?.level || null,
        type: category?.type || "",
        ranking: {
          points: 0,
          position: 0,
        },
      },
      lastLoginAt: new Date(),
    };

    const encrypted = await encryptCryptoJs(JSON.stringify(UserData));
    localStorage.setItem(LOGIN_KEY, encrypted);
    setUserLoggedData(UserData);
  };

  const setSelectedClubId = async (nextClubId) => {
    if (!nextClubId) return;
    if (!userLoggedData) return;

    const nextUserData = {
      ...userLoggedData,
      selectedClubId: nextClubId,
    };

    await persistUserData(nextUserData);
    setUserLoggedData(nextUserData);
  };

  const updateAuthPatch = async (patch = {}) => {
    if (!userLoggedData) return;
    if (!patch || typeof patch !== "object") return;

    const nextUserData = {
      ...userLoggedData,
      ...patch,
    };

    await persistUserData(nextUserData);
    setUserLoggedData(nextUserData);
  };

  const logout = () => {
    localStorage.removeItem(LOGIN_KEY);
    setUserLoggedData(null);
  };

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signInWithToken,
        updateUserFromPlayer,
        setSelectedClubId,
        updateAuthPatch,
        logout,
        userLoggedData,
        isAuthLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.any,
};

export default AuthContext;
